/**
 * Share-link frame for every `/e/:shareToken/*` page.
 *
 * The frame is deliberately public: the overview and the recommendation pages
 * must render for someone who only opened the link. Only the actions that write
 * data need a session, and those routes send an anonymous visitor through the
 * sign-in redirect while keeping the current path.
 *
 * Child pages read the resolved event, the viewer state and the token from the
 * outlet context instead of fetching the same payload again, so the public
 * section has exactly one definition of "which event is this".
 */

import { Link, NavLink, Outlet, useOutletContext, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { PublicEventResponse } from '@shared/types'

import { ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { EmptyState } from '../../components/ui/primitives'
import { useSharedEvent, useSharedRounds } from '../../hooks/use-shared'
import { ApiError } from '../../lib/api'
import { cityLabelKey, cn, formatLocalDateTime } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { loginRedirectPath } from '../../lib/login-redirect'

/** What a page under `/e/:shareToken` receives from this layout. */
export interface SharedOutletContext {
  shareToken: string
  event: PublicEventResponse['event']
  viewer: PublicEventResponse['viewer']
}

/** Reads the share-link context; only valid inside the frame below. */
export function useSharedOutlet(): SharedOutletContext {
  return useOutletContext<SharedOutletContext>()
}

interface NavItem {
  to: string
  label: string
  /** Only the overview index should match exactly. */
  end?: boolean
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'rounded-full border px-3 py-1 text-xs transition-colors',
    isActive
      ? 'border-primary bg-primary text-primary-foreground'
      : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
  )
}

/**
 * Renders the share frame and the matched page.
 *
 * The vote entry only appears once a round exists: the route is `/vote/:round`
 * and there is nothing to rank before the first recommendation run.
 */
export function SharedLayout() {
  const { t } = useTranslation()
  const { shareToken } = useParams()
  const eventQuery = useSharedEvent(shareToken)
  const roundsQuery = useSharedRounds(shareToken)

  const locale = currentLocale()

  if (!shareToken || eventQuery.isPending) {
    return (
      <div className="container-page">
        <LoadingBlock label={t('common.loading')} />
      </div>
    )
  }

  if (eventQuery.isError || !eventQuery.data) {
    const isMissing = eventQuery.error instanceof ApiError && eventQuery.error.code === 'not_found'

    return (
      <div className="container-narrow">
        {isMissing ? (
          <EmptyState
            title={t('common.notFound')}
            description={t('error.not_found')}
            action={
              <Button asChild className="mt-2">
                <Link to="/">{t('nav.home')}</Link>
              </Button>
            }
          />
        ) : (
          <ErrorBlock
            className="mt-8"
            title={t('error.internal_error')}
            onRetry={() => void eventQuery.refetch()}
            retryLabel={t('common.retry')}
          />
        )}
      </div>
    )
  }

  const { event, viewer } = eventQuery.data
  const latestRound = roundsQuery.data?.rounds[0]
  const archived = event.status === 'archived'

  // Reading the overview, the rounds and the calendar needs no session; voting
  // and the discussion do, so an anonymous visitor is not offered links that
  // would only bounce them to the sign-in page.
  const navItems: NavItem[] = [
    { to: `/e/${shareToken}`, label: t('overview.navOverview'), end: true },
    { to: `/e/${shareToken}/recommendations`, label: t('recommend.title') },
  ]
  if (latestRound && viewer.isAuthenticated) {
    navItems.push({
      to: `/e/${shareToken}/vote/${latestRound.round}`,
      label: t('vote.title'),
    })
  }
  if (viewer.isAuthenticated) {
    navItems.push({ to: `/e/${shareToken}/discuss`, label: t('discuss.title') })
  }
  navItems.push({ to: `/e/${shareToken}/calendar`, label: t('calendar.title') })

  const context: SharedOutletContext = { shareToken, event, viewer }

  return (
    <div className="container-page flex flex-col gap-6">
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{event.title}</h1>
              <Badge variant="secondary">
                {t(
                  // A draft is the organiser's own working state; to everybody
                  // else the outing is simply still collecting answers.
                  event.status === 'draft' && !viewer.isOrganizer
                    ? 'status.draftShared'
                    : `status.${event.status}`,
                )}
              </Badge>
              {viewer.isOrganizer ? (
                <Badge variant="outline">{t('overview.organizerBadge')}</Badge>
              ) : null}
              {viewer.isJoined ? <Badge variant="success">{t('join.joinedTitle')}</Badge> : null}
            </div>
            <p className="text-muted-foreground text-sm">
              {t(cityLabelKey(event.city))} · {formatLocalDateTime(event.eventDateStart, locale)}
            </p>
          </div>

          {viewer.isOrganizer ? (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/me/events/${event.id}`}>{t('overview.organizerManage')}</Link>
            </Button>
          ) : archived ? (
            <Badge variant="outline">{t('events.archivedHint')}</Badge>
          ) : viewer.isJoined ? (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/e/${shareToken}/join`}>{t('join.update')}</Link>
            </Button>
          ) : (
            <Button size="sm" asChild>
              <Link
                to={
                  viewer.isAuthenticated
                    ? `/e/${shareToken}/join`
                    : loginRedirectPath(`/e/${shareToken}/join`, '')
                }
              >
                {t('common.join')}
              </Link>
            </Button>
          )}
        </div>

        <nav aria-label={t('overview.navLabel')} className="flex flex-wrap items-center gap-2">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <Outlet context={context} />
    </div>
  )
}
